import React, { useContext } from 'react';
import Helmet from 'react-helmet';

import { NavLink } from 'react-router-dom';
import {
    learnSummitMoreLink,
    openAccountLink,
    conversionProcesLink
} from '../../utils/navigation';

import bondsImg from '../../images/BondFund.jpg';
import stockImg from '../../images/StockFund.jpg';
import allocImg from '../../images/AssetAllocation.jpg';
import monmkImg from '../../images/MoneyMarket.jpg';
import morngImg from '../../images/Morningstar.jpg';
import unlockImg from '../../images/unlock-icon.svg';
import summitImg from '../../images/summit.svg';
import iClassImg from '../../images/IClassFund.jpg';
import closedImg from '../../images/ClosedFund.jpg';

import Breadcrumb from '../Breadcrumb/Breadcrumb';
import DirectlandingPageMeta from './DirectlandingPageMeta';
import SelectFundsLandingPage from './SelectFundsLandingPage';
import TargetDateFundLandingPage from './TargetDateFundLandingPage';
import { UserContext } from '../WithUserState/WithUserState';
import { CANONICAL_PATH } from '../../utils/constants';
import getConfig from '../../config';

import styles from './DirectLandingPage.css';
import Claims from '../Claims/Claims';
import { tierTypeIds } from '../../summit-program/summit';
import ScreenerSummitMessage from '../SummitMessage/ScreenerSummitMessage';

const iclassFundsText = (summitTier, pathname, loggedIn) => {
    if (
        [tierTypeIds.CORE, tierTypeIds.SELECT, tierTypeIds.NONE].includes(
            summitTier
        )
    ) {
        return (
            <React.Fragment>
                <div className="iclass-disclaimer">
                    <img
                        style={{
                            marginRight: 10
                        }}
                        src={summitImg}
                        alt="summit"
                    />
                    <span>
                        One way to get preferred access to lower-cost I Class
                        shares is by achieving an appropriate status in the
                        Summit Program.
                    </span>
                    &nbsp;
                    {loggedIn ? (
                        <a
                            className="learnmore-summit"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={learnSummitMoreLink(pathname)}
                        >
                            Learn about Summit.
                        </a>
                    ) : (
                        <a
                            className="learnmore-summit"
                            rel="noopener noreferrer"
                            href={`${getConfig(
                                'envDomain'
                            )}/usis/login/?pdurl=/v0/ciam-public/me%3fsdurl=${getConfig(
                                'envDomain'
                            )}/personal-investing/tools/fund-research/iclass-shares`}
                        >
                            Log in to see if you qualify.
                        </a>
                    )}
                </div>
                <style jsx>{styles}</style>
            </React.Fragment>
        );
    }

    if (
        summitTier === tierTypeIds.PERSONAL_SERVICE ||
        summitTier === tierTypeIds.ENHANCED_PERSONAL_SERVICE
    ) {
        return (
            <React.Fragment>
                <div className="iclass-disclaimer">
                    <img
                        style={{
                            marginRight: 10
                        }}
                        src={unlockImg}
                        alt="unlock"
                    />
                    <span>
                        You have preferred access to lower-cost I Class shares
                        ($50,000 minimum, per fund) and closed funds because of
                        your Summit Program status.
                    </span>
                </div>
                <style jsx>{styles}</style>
            </React.Fragment>
        );
    }
    return '';
};

const closedFundsText = (summitTier, pathname, loggedIn) => {
    if ([tierTypeIds.CORE, tierTypeIds.NONE].includes(summitTier)) {
        return (
            <React.Fragment>
                <div className="iclass-disclaimer">
                    <img
                        style={{
                            marginRight: 10
                        }}
                        src={summitImg}
                        alt="summit"
                    />
                    <span>
                        One way to get preferred access to closed funds is by
                        achieving an appropriate status in the Summit Program.
                    </span>
                    &nbsp;
                    {loggedIn ? (
                        <a
                            className="learnmore-summit"
                            target="_blank"
                            rel="noopener noreferrer"
                            href={learnSummitMoreLink(pathname)}
                        >
                            Learn about Summit.
                        </a>
                    ) : (
                        <a
                            className="learnmore-summit"
                            rel="noopener noreferrer"
                            href={`${getConfig(
                                'envDomain'
                            )}/usis/login/?pdurl=/v0/ciam-public/me%3fsdurl=${getConfig(
                                'envDomain'
                            )}/personal-investing/tools/fund-research/closed-funds`}
                        >
                            Log in to see if you qualify.
                        </a>
                    )}
                </div>
                <style jsx>{styles}</style>
            </React.Fragment>
        );
    }

    if (
        [
            tierTypeIds.SELECT,
            tierTypeIds.PERSONAL_SERVICE,
            tierTypeIds.ENHANCED_PERSONAL_SERVICE
        ].includes(summitTier)
    ) {
        return (
            <React.Fragment>
                <div className="iclass-disclaimer">
                    <img
                        style={{
                            marginRight: 10
                        }}
                        src={unlockImg}
                        alt="unlock"
                    />
                    <span>
                        You have preferred access to closed funds because of
                        your Summit Program status.
                    </span>
                </div>
                <style jsx>{styles}</style>
            </React.Fragment>
        );
    }
    return '';
};

const subPageDynamicMessage = () => (
    <React.Fragment>
        <div className="iclass-disclaimer">
            <ScreenerSummitMessage />
        </div>
        <style jsx>{styles}</style>
    </React.Fragment>
);

const DirectLandingPage = ({ pathname, hashlink }) => {
    const { summitTier, loggedIn } = useContext(UserContext);

    return (
        <div className="wrapper">
            <div className="directlandingpage">
                {pathname === '/stock-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/stock-funds`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Stock Funds" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Stock Funds" />
                                <h1>Stock Funds</h1>
                                <h2>
                                    Invest in funds focused on delivering strong
                                    performance at a great value
                                </h2>
                                <blockquote>
                                    <Claims
                                        pathToClaim="/personal-investing/funds/claims/stock-funds.model.json"
                                        styles={styles}
                                    />
                                </blockquote>
                                <p>
                                    Choose from a wide variety of stock funds
                                    that offer long-term growth potential
                                    balanced with low expenses. So you can be
                                    confident your money is working hard for
                                    you.
                                </p>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    className="flipped"
                                    alt="Invest With Confidence"
                                    src={stockImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}

                {pathname === '/bond-funds' && (
                    <React.Fragment>
                        <DirectlandingPageMeta pagename="Bond Funds" />
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/bond-funds`}
                            />
                        </Helmet>
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Bond Funds" />
                                <h1>Bond Funds</h1>
                                <h2>
                                    Balance your stock portfolio&apos;s risk --
                                    and potentially gain regular income
                                </h2>
                                <p>
                                    If you&apos;d like to generate income,
                                    reduce risk from a stock-heavy portfolio, or
                                    potentially decrease your tax burden, our
                                    bond funds may be right for your portfolio.
                                </p>
                                <p>
                                    As bond markets become more complex, our
                                    seasoned professionals carefully assess
                                    investment opportunities across the globe
                                    for your portfolio.
                                </p>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={bondsImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}

                {pathname === '/money-market-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/money-market-funds`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Money Market Funds" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Money Market Funds" />
                                <h1>Money Market Funds</h1>
                                <h2>
                                    Keep your money accessible with low-risk
                                    money market funds
                                </h2>

                                <p>
                                    These funds let you easily access your money
                                    when you need it, whether you are saving for
                                    the short term or reserving it for a future
                                    investment.
                                </p>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={monmkImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}

                {pathname === '/asset-allocation-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/asset-allocation-funds`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Asset Allocation Funds" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Asset Allocation Funds" />
                                <h1>Asset Allocation Funds</h1>
                                <h2>
                                    Save time with these convenient, all-in-one
                                    funds managed by investment professionals
                                </h2>

                                <p>
                                    Asset allocation funds provide a diversified
                                    portfolio in a single investment and are
                                    rebalanced regularly. Experienced investment
                                    professionals help keep the fund portfolio
                                    on track in response to market activity.
                                </p>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={allocImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}

                {pathname === '/morningstar-4-and-5-star-rated-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/morningstar-4-and-5-star-rated-funds`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Morningstar 4- & 5-Star Rated Funds" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Morningstar 4- and 5-Star Rated Funds" />
                                <h1>Morningstar 4- and 5-Star Rated Funds</h1>
                                <h2>
                                    Invest in funds that have received top
                                    ratings from a leader in independent
                                    investment research.
                                </h2>
                                <blockquote className="claim">
                                    <Claims
                                        pathToClaim="/personal-investing/funds/claims/morningstar-4-and-5-star.model.json"
                                        styles={styles}
                                    />
                                </blockquote>
                                <p>
                                    Morningstar rankings are based on their
                                    risk-adjusted returns. Our expert investment
                                    professionals focus on maximizing returns
                                    while striving to carefully manage risk, to
                                    help you be more confident in your
                                    investments.
                                </p>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="img-col morningstar">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={morngImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
                {pathname === '/target-date-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/target-date-funds`}
                            />
                        </Helmet>
                        <TargetDateFundLandingPage hashlink={hashlink} />
                    </React.Fragment>
                )}
                {pathname === '/historical-performance' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}//historical-performance`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Historical Performance" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Historical Performance" />

                                <h1>Historical Performance</h1>
                                <h2>
                                    Get detailed information on T. Rowe Price
                                    mutual fund investment performance, along
                                    with fees and expenses.
                                </h2>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name, or use the &quot;Compare Funds&quot;
                                    link to see a side-by-side comparison.
                                </p>
                                <p>
                                    <a
                                        id="daily-prices"
                                        href={`${
                                            window.location.origin
                                        }/personal-investing/tools/fund-research/daily-prices`}
                                    >
                                        View Daily Prices
                                    </a>{' '}
                                    |{' '}
                                    <a
                                        id="compare-link"
                                        href={`${
                                            window.location.origin
                                        }/personal-investing/tools/fund-research/compare`}
                                    >
                                        Compare Funds
                                    </a>
                                </p>
                            </div>
                        </div>
                        <div className="img-col" />
                    </React.Fragment>
                )}
                {pathname === '/daily-prices' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/daily-prices`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Daily Prices" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Daily Prices" />
                                <h1>Daily Prices</h1>
                                <h2>
                                    View closing prices for T. Rowe Price mutual
                                    funds.
                                </h2>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name, or use the &quot;Compare Funds&quot;
                                    link to see a side-by-side comparison.
                                </p>
                                <p>
                                    <a
                                        id="daily-prices"
                                        href={`${
                                            window.location.origin
                                        }/personal-investing/tools/fund-research/historical-performance`}
                                    >
                                        View Historical Performance
                                    </a>{' '}
                                    |{' '}
                                    <a
                                        id="compare-link"
                                        href={`${
                                            window.location.origin
                                        }/personal-investing/tools/fund-research/compare`}
                                    >
                                        Compare Funds
                                    </a>
                                </p>
                            </div>
                        </div>
                        <div className="img-col" />
                    </React.Fragment>
                )}
                {pathname === '/iclass-shares' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/iclass-shares`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="IClass Shares" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="I Class Shares" />
                                <h1>I Class Shares</h1>
                                <h2>
                                    <Claims
                                        pathToClaim="/personal-investing/funds/claims/i-class-shares-percentage.model.json"
                                        styles={styles}
                                    />
                                </h2>
                                <blockquote className="claim">
                                    <Claims
                                        pathToClaim="/personal-investing/funds/claims/i-class-shares-example.model.json"
                                        styles={styles}
                                    />
                                </blockquote>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                                <p>
                                    Automated conversions: Existing shareholders
                                    of T. Rowe Price funds – Investor Class who
                                    meet I Class eligibility of $500,000 or more
                                    per fund or who qualify for I Class shares
                                    due to their Summit Program membership
                                    status will have eligible Investor Class
                                    holdings automatically converted to I Class
                                    shares on a periodic basis.&nbsp;<a
                                        id="conversionprocess-dl"
                                        href={conversionProcesLink}
                                    >
                                        Learn more about the conversion process
                                    </a>
                                </p>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={iClassImg}
                                />
                            </div>
                        </div>
                    </React.Fragment>
                )}
                {pathname === '/closed-funds' && (
                    <React.Fragment>
                        <Helmet>
                            <link
                                rel="canonical"
                                href={`${CANONICAL_PATH}/closed-funds`}
                            />
                        </Helmet>
                        <DirectlandingPageMeta pagename="Closed Funds" />
                        <div className="text-col">
                            <div className="text-col-body">
                                <Breadcrumb name="Closed Funds" />
                                <h1>Closed Funds</h1>
                                <h2>
                                    {' '}
                                    Get preferred access to closed funds, a
                                    benefit only available when you buy directly
                                    from T.&nbsp;Rowe Price.
                                    <sup>&#8225;</sup>
                                </h2>
                                <p>
                                    These funds have been closed to new
                                    investors due to high demand, to preserve
                                    the fund’s ability to invest consistent with
                                    its objective, or for other reasons.
                                </p>
                                <blockquote className="claim">
                                    <Claims
                                        pathToClaim="/personal-investing/funds/claims/closed-funds.model.json"
                                        styles={styles}
                                    />
                                </blockquote>
                                <p>
                                    To get a more detailed profile, including
                                    standardized returns as of the most recent
                                    calendar quarter end, click on any fund
                                    name.
                                </p>
                            </div>
                        </div>
                        <div className="img-col">
                            <div className="container">
                                <img
                                    alt="Invest With Confidence"
                                    src={closedImg}
                                />
                                <div>
                                    <p className="browse-all">
                                        <NavLink exact to="/">
                                            Browse all T. Rowe Price Mutual
                                            Funds
                                        </NavLink>
                                    </p>
                                    <div className="ready-wrapper">
                                        <span className="intro">
                                            Ready to get started?<br />
                                        </span>
                                        <a
                                            id="openaccout-dl"
                                            href={openAccountLink()}
                                        >
                                            Open an Account
                                        </a>&nbsp;
                                        <span>or call 866-264-5579</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
                {pathname === '/select-funds' && <SelectFundsLandingPage />}
            </div>
            {pathname === '/iclass-shares' &&
                iclassFundsText(summitTier, pathname, loggedIn)}
            {pathname === '/closed-funds' &&
                closedFundsText(summitTier, pathname, loggedIn)}
            {(pathname === '/stock-funds' ||
                pathname === '/select-funds' ||
                pathname === '/bond-funds' ||
                pathname === '/morningstar-4-and-5-star-rated-funds' ||
                pathname === '/target-date-funds' ||
                pathname === '/asset-allocation-funds' ||
                pathname === '/money-market-funds' ||
                pathname === '/daily-prices' ||
                pathname === '/historical-performance') &&
                subPageDynamicMessage()}
            <style jsx>{styles}</style>
        </div>
    );
};
export default DirectLandingPage;
